import { FormEvent, ReactElement, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Alert, Button, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { OrganizationLayout } from '../../../../components/OrganizationLayout';
import { RolesUtils, RoleInfo } from '../../../../utils/RolesUtils';
import { UserRoles } from '../../../../api/queries';
import { useGetOrganizationUsersQuery } from '../../../../hooks';
import { useUpdateUserRolesMutation } from '../../../../hooks/mutations';
import { Routes } from '../../../../enums/Routes';
import { buildRoute } from '../../../../utils/routeBuilder';
import { UserSummary } from '../components/UserSummary';
import { RoleChoices } from '../components/RoleChoices';
import { RoleGuide } from '../components/RoleGuide';
import { SafetyRepFields } from '../components/SafetyRepFields';
import { EMPTY_SAFETY_REP, safetyRepError, sameAssignment } from '../safetyRepView';
import classes from '../Users.module.css';

export const EditUserPage = (): ReactElement => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const updateUserRolesMutation = useUpdateUserRolesMutation();
  const { data: organizationUsers, isLoading, isError } = useGetOrganizationUsersQuery();

  const user = organizationUsers?.find((u) => u.id === Number(userId));
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [safetyRep, setSafetyRep] = useState(EMPTY_SAFETY_REP);
  const [tried, setTried] = useState(false);

  // Initialize selected roles when user data is loaded
  useEffect(() => {
    if (user) {
      setSelectedRoles(RolesUtils.filterAdministerableRoles(user.roles));
      setSafetyRep(
        user.safetyRep
          ? { isMain: user.safetyRep.isMain, teamIds: user.safetyRep.teams.map((team) => team.id) }
          : EMPTY_SAFETY_REP,
      );
    }
  }, [user]);

  const orgAdminCount = organizationUsers?.filter((u) => u.roles.includes(UserRoles.ORG_ADMIN)).length ?? 0;
  // Den siste organisasjonsadministratoren kan ikke miste rollen
  const isLastAdmin = selectedRoles.includes(UserRoles.ORG_ADMIN) && orgAdminCount <= 1;
  const allRoles: RoleInfo[] = RolesUtils.getInvitableRoles();
  const backTo = user ? buildRoute(Routes.OrganizationViewUser, { userId: user.id }) : Routes.OrganizationUsersOverviewPage;

  const isSafetyRep = selectedRoles.includes(UserRoles.SAFETY_REP);
  const savedSafetyRep = user?.safetyRep
    ? { isMain: user.safetyRep.isMain, teamIds: user.safetyRep.teams.map((team) => team.id) }
    : EMPTY_SAFETY_REP;
  const teamsError = tried && isSafetyRep && safetyRepError(safetyRep) !== null;
  const hasChanges = user
    ? JSON.stringify([...selectedRoles].sort()) !==
        JSON.stringify([...RolesUtils.filterAdministerableRoles(user.roles)].sort()) ||
      (isSafetyRep && !sameAssignment(safetyRep, savedSafetyRep))
    : false;

  const handleToggleRole = (roleKey: string) => {
    if (roleKey === UserRoles.ORG_ADMIN && isLastAdmin) return;
    setSelectedRoles((prev) => (prev.includes(roleKey) ? prev.filter((r) => r !== roleKey) : [...prev, roleKey]));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!user || !hasChanges || updateUserRolesMutation.isPending) return;
    if (isSafetyRep && safetyRepError(safetyRep) !== null) {
      setTried(true);
      return;
    }

    // Ensure at least MEMBER role if no roles selected
    const rolesToSave = selectedRoles.length > 0 ? selectedRoles : [UserRoles.MEMBER];

    updateUserRolesMutation.mutate(
      { userId: user.id, data: { roles: rolesToSave, safetyRep: isSafetyRep ? safetyRep : undefined } },
      {
        onSuccess: () => {
          toast.success(t('users.edit.toast.success', { email: user.email }));
          navigate(backTo);
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || t('users.edit.toast.error'));
        },
      },
    );
  };

  const content = () => {
    if (isLoading) {
      return (
        <div aria-busy="true">
          <Skeleton variant="rectangle" width="100%" height={360} />
        </div>
      );
    }
    if (isError || !user) {
      return (
        <Alert data-color="danger">
          <Paragraph>{t('users.edit.errorMessage')}</Paragraph>
        </Alert>
      );
    }

    const hasInvitableRole = selectedRoles.some((role) => allRoles.some((info) => info.key === role));

    return (
      <form onSubmit={handleSubmit} className={classes.form}>
        <section aria-label={t('users.edit.pageTitle')} className={classes.card}>
          <UserSummary user={user} />
          <RoleChoices
            idPrefix="edit-role"
            legend={t('users.edit.sections.roles')}
            description={t('organizationPage.users.editRolesHint')}
            roles={allRoles}
            selected={selectedRoles}
            disabled={updateUserRolesMutation.isPending}
            lockedReason={(role) =>
              role.key === UserRoles.ORG_ADMIN && isLastAdmin ? t('users.edit.warnings.lastAdmin') : undefined
            }
            onToggle={handleToggleRole}
          />
          {isSafetyRep && (
            <SafetyRepFields
              idPrefix="edit-safety-rep"
              value={safetyRep}
              onChange={setSafetyRep}
              disabled={updateUserRolesMutation.isPending}
              error={teamsError ? t('organizationPage.users.safetyRep.teamsError') : undefined}
            />
          )}
          {!hasInvitableRole && (
            <Paragraph data-size="sm" className={classes.note}>
              {t('users.edit.info.noRolesSelected')}
            </Paragraph>
          )}
          {user.roles.includes(UserRoles.PARTNER_ADMIN) && (
            <Paragraph data-size="sm" className={classes.note}>
              {t('organizationPage.users.partnerNote')}
            </Paragraph>
          )}
        </section>
        <div className={classes.actions}>
          <Button type="submit" disabled={!hasChanges || updateUserRolesMutation.isPending}>
            {updateUserRolesMutation.isPending ? t('users.edit.buttons.saving') : t('users.edit.buttons.save')}
          </Button>
          <Button variant="secondary" onClick={() => navigate(backTo)} disabled={updateUserRolesMutation.isPending}>
            {t('users.edit.buttons.cancel')}
          </Button>
        </div>
        <RoleGuide roles={allRoles} />
      </form>
    );
  };

  return (
    <OrganizationLayout
      breadcrumbs={[
        { label: t('organizationPage.tabs.users'), to: Routes.OrganizationUsersOverviewPage },
        ...(user ? [{ label: user.email, to: buildRoute(Routes.OrganizationViewUser, { userId: user.id }) }] : []),
        { label: t('breadcrumbs.editUser'), to: '' },
      ]}
    >
      {content()}
    </OrganizationLayout>
  );
};
